import * as Commons from "oc-commons/Commons";
import * as Debugs from "oc-threejs/Debugs";
import _ from "lodash";

/**
 * Stellt ein Undo / Redo Feature für Configurations bereit. Configurations werden automatisch
 * gespeichert, wenn das changedConfiguration - Event auf der APP ausgelöst wird.
 *
 * Die APP registriert standardmäßig einen Undohandler als APP.undoHandler.
 *
 * ACHTUNG: Wird erst aktiv durch APP.settings.undo.enabled=true
 *
 * Falls die Undo-History sich geändert hat, wird ein **undoHistoryChanged** Event auf der APP
 * ausgelöst.
 *
 * @example
 * document.querySelector('#undoButton').addEventListener('tap',()=>{
 *    APP.undoHandler.undo();
 * });
 * document.querySelector('#redoButton').addEventListener('tap',()=>{
 *    APP.undoHandler.redo();
 * });
 * APP.on('undoHistoryChanged',()=>{
 *    Doms.disable(document.querySelector('#undoButton'),!APP.undoHandler.canUndo());
 *    Doms.disable(document.querySelector('#redoButton'),!APP.undoHandler.canRedo());
 * });
 */
export class UndoHandler {
  recallCounter: number;
  stack: any[];
  pointer: number;
  enabled: boolean;
  debounceTimer: number;
  recallDelay: number;
  recordNextChance: boolean;
  saveCounter: number;

  /** */
  constructor() {
    this.recallCounter = 0;
    this.stack = [];
    this.pointer = -1;
    this.enabled = true;
    this.debounceTimer = 1000;
    this.recallDelay = 1000;
    this.recordNextChance = true;

    // speichert automatisch die Configuration bei Änderungen
    // ein höherer Wert hier (2s) ist sinnvoll, um rückkoppelnde Änderungen
    // abzuwarten (diese treten z.B. im Zaunplaner auf)
    const saveDebounced = _.debounce(() => this.save(), this.debounceTimer, {
      leading: false,
      trailing: true,
    });
    this.saveCounter = 0;
    const maybeSave = () => {
      // console.log("UndoHandler: maybeSave");
      if (this.recordNextChance && this.saveCounter == 0) {
        // console.log("UndoHandler: recordNextChance = false");
        this.saveCounter++;
        try {
          if (this.recallCounter == 0) {
            // console.log("UndoHandler: saveDebounced()");
            saveDebounced();
          } else {
            // console.log("UndoHandler: recallCounter > 0");
          }
        } finally {
          this.saveCounter--;
        }
      } else {
        // console.log(
        //   `UndoHandler: skip maybeSave (recordNextChance=${this.recordNextChance}, saveCounter=${this.saveCounter})`,
        // );
      }
    };

    APP.on("userActionWithStateChange", () => {
      // console.log("UndoHandler: recordNextChance = true");
      this.recordNextChance = true;
    });
    APP.on("loadedConfiguration", () => {
      // console.log("UndoHandler: loadedConfiguration");
      this.reset();
      this.save();
    });
    APP.on("update3d", () => {
      // console.log("UndoHandler: update3d");
      maybeSave();
    });
  }

  reset(): void {
    this.recallCounter = 0;
    this.stack = [];
    this.pointer = -1;
  }

  /**
   * wird über das changedConfiguration-Event aufgerufen
   */
  save(): void {
    if (
      !APP.settings?.undo?.enabled ||
      this.recallCounter != 0 ||
      !this.enabled ||
      !this.recordNextChance
    ) {
      // console.log(
      //   "UndoHandler: !APP.settings?.undo?.enabled || this.recallCounter != 0 || !this.enabled",
      // );
      return;
    }
    this.recordNextChance = false;

    const newConfig = this.getStageAsConfigurationForUndo();
    if (newConfig == null) {
      // console.log("UndoHandler: !newConfig");
      return;
    }

    const lastConfig = this.stack[this.pointer];

    // der Vergleich, zusammen mit cloneDeep ist wichtig, damit auch bei debounced changedConfigurations
    // verhindert wird, dass der Undohandler durch sich selbst ausgelöste Änderungen ignoriert
    if (this.isEqualConfiguration(lastConfig, newConfig)) {
      // console.log("UndoHandler: isEqualConfiguration(lastConfig, newConfig)");
      return;
    }

    // Commons.logDeepDiffWithValues(lastConfig, newConfig);

    // Zu Debugzwecken kann es hilfreich sein, sich die Unterschiede vorher/nachher anzuzeigen
    // console.log("*** Undo-Entry because ", Commons.deepDiff(lastConfig, newConfig));
    // debugger;

    // Remove all cfgs after the current position
    this.stack.splice(this.pointer + 1, this.stack.length);

    if (this.stack.length >= APP.settings.undo.max) {
      this.stack.shift();
    }

    this.stack.push(Commons.cloneDeep(newConfig));
    this.pointer = this.stack.length - 1;
    APP.trigger("undoHistoryChanged");
    // console.log("UndoHandler: stack", this.stack.length);
  }

  getStageAsConfigurationForUndo(): any {
    return APP.getStageAsConfiguration();
  }

  isEqualConfiguration(lastConfig: any, newConfig: any): boolean {
    return Commons.isEqualEpsilon(lastConfig, newConfig);
  }

  /** */
  canUndo(): boolean {
    return this.pointer > 0;
  }

  /** */
  undo(): void {
    if (!this.canUndo()) {
      return;
    }
    this.pointer--;
    this._setConfigurationFromPointer();
  }

  /** */
  canRedo(): boolean {
    return this.pointer < this.stack.length - 1;
  }

  /** */
  redo(): void {
    if (!this.canRedo()) {
      return;
    }
    this.pointer++;
    this._setConfigurationFromPointer();
  }

  /**
   */
  _setConfigurationFromPointer(): void {
    try {
      this.recallCounter++;
      APP.setStageFromConfiguration(Commons.cloneDeep(this.stack[this.pointer]), null, true);
      APP.dirty();
      // console.log("undoHandler _setConfigurationFromPointer Pointer", this.pointer);
    } finally {
      // es kann sein, dass das Setzen der Konfiguration nicht idempotent ist, also ein weiteres Update auslöst.
      // Hiermit wird verhindert, dass das Re- oder Undo wiederum ein Save auslöst
      _.delay(() => this.recallCounter--, this.recallDelay);
    }
  }
}
